import { api as apiService, ApiService } from './api'

export enum enStatusRoute {
  CONCLUIDA = 'concluída',
  INCOMPLETA = 'incompleta',
}

export interface IGetRoutes {
  data: {
    [key: string]: IRoutes
  }
}

export interface IRoutes {
  orders: [
    {
      order_id: string
      boxes: number
      quantity_reprint: number
      date_printed: string
    },
  ]
  status: enStatusRoute
  color_route: string
}

export interface IOverRouteParams {
  scheduledDate?: string
  controller: AbortController
}

export interface IRouteOverResponse {
  route: number
  real_boxes_number: number
  capacity_vehicle: number
  license_plate: string | null
  excess_boxes: number
  route_above: IRouteLimit
}

export interface IRouteAboveLimit {
  totalCount: number
  pageIndex: number
  pageSize: number
  results: IRouteLimit[]
}

export interface IRouteAboveLimitParams {
  routes?: string
  page?: number
  pageSize?: number
}

export interface IRouteOverflowDetailChange {
  order_id: string
  previous_box_count?: number | null
  current_box_count: number
}

export interface IRouteOverflowDetails {
  changes?: IRouteOverflowDetailChange[]
  previous_license_plate?: string | null
  current_license_plate?: string | null
  order_box_count?: number | null
  from_route?: number | null
  previous_box_count?: number | null
  current_box_count?: number | null
}

export interface IRouteLimit {
  id: number
  route: number
  status: 'pending' | 'resolved' | 'allowed_over_capacity'
  active: boolean
  detected_at: string | null
  resolved_at: string | null
  allowed_over_capacity_at: string | null
  allowed_over_capacity_by: string | null
  allowed_over_capacity_reason: string | null
  cause_type: string
  cause_label: string
  trigger_order_id: string | null
  trigger_order_box_count_id: string | null
  trigger_source: string | null
  triggered_by: string | null
  previous_total: number | null
  current_total: number | null
  previous_capacity: number | null
  current_capacity: number | null
  cause_details: IRouteOverflowDetails
  trigger_event?: IOrderBoxCountEvent | null
  trigger_events?: IOrderBoxCountEvent[]
  detected_total?: number | null
  detected_capacity?: number | null
  resolved_total?: number | null
  resolved_capacity?: number | null
  resolution_type?: string | null
  resolution_order_id?: string | null
  resolution_order_box_count_id?: string | null
  resolution_source?: string | null
  resolved_by?: string | null
  resolution_label?: string | null
  actions?: IRouteOverflowAction[]
  route_number?: number | null
  license_plate?: string | null
  scheduled_date?: string | null
}

export interface IRouteOverflowAction {
  id: number
  incident: number
  action_type: string
  order_id: string | null
  order_box_count_id: string | null
  source: string | null
  actor: string | null
  previous_total: number | null
  current_total: number | null
  previous_capacity: number | null
  current_capacity: number | null
  previous_box_count: number | null
  current_box_count: number | null
  details: IRouteOverflowDetails
  occurred_at: string
}

export interface IOrderBoxCountEvent {
  id: string
  boxes_count: number
  source: string
  source_label: string
  source_reference: string | null
  version: number
  created_at: string
  created_by: string | null
  created_by_id: number | null
}

export interface IRouteCapacityOrder {
  loading_conference_id: number
  order_id: string
  client_name_or_trade: string | null
  boxes_count: number
  current_version: number
  current_event: IOrderBoxCountEvent | null
  already_reduced: boolean
  reductions_count: number
  last_reduction: IOrderBoxCountEvent | null
}

export interface IRouteCapacityDetail {
  route: number
  license_plate: string | null
  capacity_vehicle: number | null
  boxes_count: number
  excess_boxes: number
  incident: IRouteLimit | null
  orders: IRouteCapacityOrder[]
  reduction_event?: IOrderBoxCountEvent
}

export interface IRouteOverflowHistory {
  totalCount: number
  pageIndex: number
  pageSize: number
  results: IRouteLimit[]
}

class RouteService {
  constructor(private readonly api: ApiService) {}

  async getRoutes(scheduledDate: string, controller: AbortController): Promise<IGetRoutes> {
    return this.api.get(carambolaUrl + '/routes/orders-by-route', {
      params: { scheduledDate },
      signal: controller.signal,
    })
  }

  async postReprintRoute(routeId: string, orderId?: string): Promise<{ message: string }> {
    return this.api.post(carambolaUrl + '/print/reprint-by-route', {
      routeId,
      orderId,
    })
  }

  async getOverRoutes(params: IOverRouteParams): Promise<{ data: IRouteOverResponse[] }> {
    return this.api.get(carambolaUrl + '/routes/route-over-boxes', {
      params,
      signal: params.controller.signal,
    })
  }

  async getListRouteLimit(params: IRouteAboveLimitParams): Promise<IRouteAboveLimit> {
    return this.api.get(carambolaUrl + '/routes/route-above-box-limit', {
      params,
    })
  }

  async updateRouteLimit(routeLimitId: number, data: Partial<IRouteLimit>): Promise<IRouteLimit> {
    return this.api.patch(carambolaUrl + `/routes/route-above-box-limit/${routeLimitId}`, data)
  }

  async getRouteCapacity(route: number): Promise<IRouteCapacityDetail> {
    return this.api.get(carambolaUrl + `/routes/route-capacity/${route}`)
  }

  async getRouteOverflowHistory(
    params: {
      page?: number
      pageSize?: number
      route?: number
      status?: string
      causeType?: string
      from?: string
      to?: string
      controller?: AbortController
    } = {},
  ): Promise<IRouteOverflowHistory> {
    const { controller, ...queryParams } = params
    return this.api.get(carambolaUrl + '/routes/route-capacity/history', {
      params: queryParams,
      signal: controller?.signal,
    })
  }

  async reduceRouteOrder(
    route: number,
    orderId: string,
    data: {
      boxes_count: number
      expected_version?: number
      confirm_reduction_again?: boolean
      idempotency_key?: string
    },
  ): Promise<IRouteCapacityDetail> {
    return this.api.post(
      carambolaUrl + `/routes/route-capacity/${route}/orders/${orderId}/reduce`,
      data,
    )
  }
}

const carambolaUrl = process.env.REACT_APP_API_CARAMBOLA
export const routeService = new RouteService(apiService)
